import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './login/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { PRESERVE_HTTP_ERROR } from './shared/utils/preserve-http-error.context';

const CLIENT_DEBT_WARNING_CODE = 'CLIENT_DEBT_WARNING';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, private cookieService: CookieService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        if (request.context.get(PRESERVE_HTTP_ERROR) || this.isClientDebtWarning(error)) {
          return throwError(() => error);
        }
        let message: string = '';
        switch (error.status) {
          case 403:
            message = 'ОШИБКА. У вас не требуемых прав доступа';
            break;
          case 404:
            message = 'ОШИБКА. Ресурс не найден';
            break;
          case 422:
            message = 'ОШИБКА. Предоставлены неверные данные';
            break;
          case 500:
          case 501:
          case 502:
          case 503:
            message = 'ОШИБКА. Внутренняя ошибка сервера';
            break;
          default:
            message = error.error.message;
            break;
        }
        return throwError(() => message || error.error.message);
      })
    );
  }

  private isClientDebtWarning(error: HttpErrorResponse) {
    const candidates = [
      error?.error,
      error?.error?.error,
      error?.error?.message,
      error?.error?.response
    ];
    return candidates.some((candidate) => candidate?.code === CLIENT_DEBT_WARNING_CODE);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (request.headers.has('X-Skip-Refresh-Retry')) {
      this.authService.logout();
      return throwError(() => 'Ошибка авторизации');
    }

    if (request.url.includes('/login')) {
      return throwError(() => 'Ошибка авторизации');
    }

    if (request.url.includes('/auth/logout/user')) {
      return throwError(() => 'Ошибка авторизации');
    }

    if (request.url.includes('/auth/refresh/user')) {
      this.authService.logout();
      return throwError(() => 'Ошибка авторизации');
    }

    const refreshToken = this.cookieService.get('refresh_token');

    if (!refreshToken) {
      this.authService.logout();
      return throwError(() => 'Сессия истекла. Пожалуйста, войдите снова.');
    }

    return this.authService.refreshToken().pipe(
      switchMap((response) =>
        this.authService.syncCurrentUser({ skipRefreshRetry: true }).pipe(
          switchMap(() => {
            const newRequest = request.clone({
              headers: request.headers
                .set('Authorization', `Bearer ${response.access_token}`)
                .set('X-Skip-Refresh-Retry', '1')
            });
            return next.handle(newRequest);
          })
        )
      ),
      catchError((error) => {
        this.authService.logout();
        return throwError(() => error);
      })
    );
  }
}
